import axios from 'axios';
import commonGymFunctions from '@/helpers/commonGymFunctions'
import GymEcomService from '../../views/GymEcomService';
import useLocation from '@/helpers/location.js';

// Used for to populate gym_common state on initial load and reset gym_common state on logout
const getGymCommonDefaultState = () => {
  return {
		fitnessData: null,
		loadingFitnessData: false,
		selectedDate: new Date(),
		ownEvents: false,
		ownEventsObject: false,
		selectedCategory: null,
		selectedFitnessLocation: null,
		appAuth:  null,
		customerTermsArticleId: null,
		entriesCount: null,
		userWorkouts: null,
		instructorEvents: null,
		instructorEventsObject: null,
		userAccessEvents: null,
		loadingUserWorkouts: false,
		// Result of last getCurrentPosition + isInsideAnyLocation check (used when requireLocationCheck is true)
		locationVerificationForConfirm: { isInside: false, timestamp: 0 }
  }
}

export default {
	namespaced: true,
	state: getGymCommonDefaultState(),
	getters: {
		userWorkouts: ( state ) => state.userWorkouts,
		// Get user confirmable events, updates every time when fitnessdata is fetched
		userConfirmableEvents: (state, getters, rootState) => {
			//console.log("gym_common: getters: userConfirmableEvents")
			const userConfirmableEvents = state.fitnessData?.events.filter((event) => commonGymFunctions.methods.checkIsEventConfirmableByUser(rootState.common.settings, rootState.user?.user?.personId, event))
			if(typeof(userConfirmableEvents) === "object" && userConfirmableEvents.length > 0) {
				return userConfirmableEvents
			}
			return []
		},
		GymEcomService: () => GymEcomService,

		// When requireLocationCheck is true, canUserConfirm is based on locationVerificationForConfirm
		// (set by checkCurrentLocationForConfirmation: getCurrentPositionWithTimeout + isInsideAnyLocation)
		canUserConfirm: (state, getters, rootState) => {
			if (rootState.common.settings.fitness_eventConfirm?.requireLocationCheck !== true) {
				return true
			}
			const verification = state.locationVerificationForConfirm
			if (!verification || !verification.timestamp) {
				return false
			}
			const maxAgeMinutes = rootState.common.settings.reuseLocationConfirmationLastMinutes
			const maxAgeMs = maxAgeMinutes * 60 * 1000
			const age = Date.now() - verification.timestamp
			return verification.isInside === true && age <= maxAgeMs
		}
	},
	mutations: {
		mutateGymCommonStateToDefault(state) {
			Object.assign(state, getGymCommonDefaultState())
		},
		mutateFitnessData(state, fitnessData) {
			state.fitnessData = fitnessData;
		},
		mutateSelectedDate(state, selectedDate) {
			state.selectedDate = selectedDate;
		},
		mutateLoadingFitnessData(state, loadingFitnessData) {
			state.loadingFitnessData = loadingFitnessData;
		},
		mutateSelectedCategory(state, selectedCategory) {
			state.selectedCategory = selectedCategory;
		},
		mutateSelectedFitnessLocation(state, selectedFitnessLocation) {
			state.selectedFitnessLocation = selectedFitnessLocation;
		},
		mutateHandleFitnessEvent(state, updatedEvent) {
			const eventIndex = state.fitnessData.events.findIndex(eventItem => eventItem.activityId === updatedEvent?.activityId);
		
			if (eventIndex !== -1) {
				// Merge the existing event properties with the updated event
				state.fitnessData.events[eventIndex] = {
					...state.fitnessData.events[eventIndex], // keep existing properties
					...updatedEvent, // overwrite with updated properties
				};
			}
		},
		mutateMarkAttendance(state, data) {
			const eventIndex = state.fitnessData.events.findIndex(eventItem => eventItem.activityId === data.activityId)
			const attendeeIndex = state.fitnessData.events[eventIndex].eventAttendees.findIndex(attendee => attendee.attendeeId === data.attendeeMark.attendeeId)
			state.fitnessData.events[eventIndex].eventAttendees[attendeeIndex].markedAttendance = data.attendeeMark.markedAttendance
		},
		mutateOwnEvents(state, ownEvents) {
			state.ownEvents = ownEvents;
		},
		mutateOwnEventsObject(state, ownEventsObject) {
			state.ownEventsObject = ownEventsObject;
		},
		mutateCanUserConfirm(state, canUserConfirm) {
			state.canUserConfirm = canUserConfirm;
		},
		mutateEntriesCount(state, entriesCount) {
			state.entriesCount = entriesCount;
		},
		mutateUserWorkouts(state, userWorkouts) {
			state.userWorkouts = userWorkouts;
		},
		mutateInstructorEvents(state, instructorEvents) {
			state.instructorEvents = instructorEvents;
		},
		mutateInstructorEventsObject(state, instructorEvents) {
			state.instructorEventsObject = instructorEvents;
		},
		mutateLoadingUserWorkouts(state, loading) {
			state.loadingUserWorkouts = loading;
		},
		mutateLoadingEntriesCount(state, loadingEntriesCount) {
			state.loadingEntriesCount = loadingEntriesCount
		},
		mutateLocationVerificationForConfirm(state, { isInside, timestamp }) {
			state.locationVerificationForConfirm = { isInside: !!isInside, timestamp: timestamp || 0 }
		},
		mutateRecalculateOwnEventsObject(state, rootState) {
			const ownEvents = state.fitnessData?.events?.filter(event => 
				event.eventAttendees?.find(attendee => attendee?.personId === rootState.user?.user?.personId)
			) || [];
			state.ownEventsObject = ownEvents;
		},
	},

	actions: {
		async getFitnessData({ rootGetters, commit, rootState }) {

			// WiseSettings.js useFitness controls tabs and api calls
			const useFitness = rootGetters["common/activeHost"].useFitness

			if (useFitness) {
				console.log("gym_common: getFitnessData()")

				commit('mutateLoadingFitnessData', true)

				// Get fitness data, Filter events to categories
				try {
					const fitnessData = await axios.get(`${rootGetters["common/activeHost"].ajaxUrl}?controller=ajax&fitnessdata=1&lang=${rootState.common.userLocale}&appauth=${rootGetters["common/activeHost"].appauth}`)

					// Assign category name to each event as eventCategoryName
					fitnessData.data.events = fitnessData.data.events.map(event => {
						const category = fitnessData.data.categories.find(category => category.eventCategoryId === event.eventCategoryId);
						return {
							...event,
							eventCategoryName: category ? category.name : null
						};
					})

					// If have categories
					if (fitnessData.data.categories !== false) {
						fitnessData?.data?.categories?.forEach((category, index) => {
							fitnessData.data.categories[index].events = fitnessData.data.events
							.filter(event => event.eventCategoryId === category.eventCategoryId)
						})
					}
					
					commit('mutateFitnessData', fitnessData?.data)
					
					const ownEvents = fitnessData.data.events.filter(event => event.eventAttendees?.find(attendee => attendee?.personId === rootState.user?.user?.personId))
					//Store to state.gym_common.ownEvents
					commit('mutateOwnEventsObject', ownEvents)

					const instructorEvents = fitnessData.data.events.filter(event => event.activityPersons?.find(person => person.personId === rootState.user?.user?.personId))
					// Store to state.gym_common.instructorEvents
					commit('mutateInstructorEventsObject', instructorEvents)

					commit('mutateLoadingFitnessData', false)
				} catch (error) {
					console.error(error)
					commit('mutateFitnessData', null)
					commit('mutateLoadingFitnessData', false)
					return Promise.reject(error)
				}
			}
		},

		async getUserWorkouts({rootGetters, commit}) {
			commit('mutateLoadingUserWorkouts', true)
			try {
				const data = await axios.get(`${rootGetters["common/activeHost"].restUrl}userworkouts/`)
				commit('mutateUserWorkouts', data.data)
			} catch (error) {
				console.error(error)
			}
			commit('mutateLoadingUserWorkouts', false)
		},

		async getEntriesCount({rootGetters, commit}) {
			commit('mutateLoadingEntriesCount', true)
			try {
				const data = await axios.get(`${rootGetters["common/activeHost"].restUrl}entriescount`)
				commit('mutateEntriesCount', data.data)
			} catch (error) {
				console.log(error)
			}
			commit('mutateLoadingEntriesCount', false)
		},

		async handleFitnessEvent({ rootGetters, commit, rootState }, data) {
			let isUserLocal = false;

			if (rootState.common.settings.fitness_eventEnroll?.allowBypassMaxEnrollmentsLimit === true || rootState.common.settings.fitness_eventEnroll?.allowBypassSanctionsIfLocal === true) {
				// Fetch new location
				const locationHelper = useLocation();
				const position = await locationHelper.getCurrentPositionWithTimeout();
				console.log("gym_common: handleFitnessEvent: position", position)
				isUserLocal = position != null && (await isInsideAnyLocation(position, rootState));
			}

			const eventHandleData = {
				action: data.action,
				activityId: data.event.activityId,
				hash: data.event.hash,
				isMobile: true,
				isUserLocal: isUserLocal,
				personId: rootState.user.user.personId,
				tagData: false
			}

			try {
				const { data } = await axios.post(`${rootGetters["common/activeHost"].ajaxUrl}?controller=ajax&handleevent=1&lang=${rootState.common.userLocale}&appauth=${rootGetters["common/activeHost"].appauth}`, eventHandleData)
				if (Object.keys(data)[0] === 'error') {
					return Promise.reject(data.error)
				}
				commit('mutateHandleFitnessEvent', data.event)

				commit('mutateRecalculateOwnEventsObject', rootState)

				return data
			} catch (error) {
				console.error(error)
				throw(error)
			}
		},

		async markFitnessAttendance({ rootGetters, commit, rootState }, attendee) {
			const attendeeMark = {
				attendeeId: attendee.attendeeId,
				markedAttendance: attendee.markedAttendance
			}

			try {
				const { data } = await axios.post(`${rootGetters["common/activeHost"].ajaxUrl}?controller=ajax&markattendance=1&lang=${rootState.common.userLocale}&appauth=${rootGetters["common/activeHost"].appauth}`, attendeeMark)
				if (Object.keys(data)[0] === 'error') {
					return Promise.reject(data.error)
				}
				commit('mutateMarkAttendance', { attendeeMark, activityId: attendee.activityId })

				return data
			} catch (error) {
				console.error(error)
				throw(error)
			}
		},

		/**
		 * Check current location for event confirmation: getCurrentPositionWithTimeout + isInsideAnyLocation.
		 * Called when GymEvent view is opened (and on pull-to-refresh). Stores result in state for canUserConfirm getter.
		 * @returns {Object} { success: boolean, reason: string }
		 */
		async checkCurrentLocationForConfirmation({ rootState, commit }) {
			if (rootState.common.settings.fitness_eventConfirm?.requireLocationCheck !== true) {
				return { success: true, reason: 'not_required' }
			}

			const locationHelper = useLocation()
			const permissionResult = await locationHelper.requestLocationPermission()

			if (!permissionResult.granted) {
				commit('mutateLocationVerificationForConfirm', { isInside: false, timestamp: Date.now() })
				if (!permissionResult.canRequest) {
					return { success: false, reason: 'permission_denied_permanently' }
				}
				return { success: false, reason: 'permission_denied' }
			}

			const position = await locationHelper.getCurrentPositionWithTimeout()
			if (!position) {
				commit('mutateLocationVerificationForConfirm', { isInside: false, timestamp: Date.now() })
				return { success: false, reason: 'position_unavailable' }
			}

			const isInside = await isInsideAnyLocation(position, rootState)
			commit('mutateLocationVerificationForConfirm', { isInside, timestamp: Date.now() })
			return isInside ? { success: true, reason: 'inside_location' } : { success: false, reason: 'outside_location' }
		},
	}
}



// Check if user is inside any location (async so callers can await)
const isInsideAnyLocation = async (userCurrentCordinates, rootState) => {
	let gpsInsideLocation = false
	if (userCurrentCordinates == null || !rootState.door.doorAndLocationData?.locations) {
		return gpsInsideLocation
	}
	const locationHelper = useLocation()
	const devLocation = rootState.common.settings.devLocation

	for (const location of rootState.door.doorAndLocationData.locations) {
		if (!location.locationSettings?.coordinates) continue
		const isInsideArea = locationHelper.isPositionInArea(userCurrentCordinates, location.locationSettings.coordinates)
		let isInsideDevArea = false
		if (devLocation && typeof devLocation === 'object') {
			isInsideDevArea = locationHelper.isPositionInArea(userCurrentCordinates, devLocation)
		}
		if (isInsideArea === true || isInsideDevArea === true) {
			gpsInsideLocation = true
			break
		}
	}
	return gpsInsideLocation
}	